<template>
  <div class="page-wrap">
    <div class="panel-card">

      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-shield-alt"></i>
          FedRAMP Audit Log (Syslog)
        </span>
        <button class="btn-outline" @click="loadLog()">
          <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i> Refresh
        </button>
      </div>

      <div class="toolbar">
        <div class="search-box">
          <i class="fa fa-search"></i>
          <input
            type="text"
            placeholder="Filter by user, model, event, path…"
            v-model="search"
            @keyup.enter="loadLog()"
          />
        </div>
        <select v-model.number="linesLimit" @change="loadLog()">
          <option :value="200">Last 200 lines</option>
          <option :value="500">Last 500 lines</option>
          <option :value="1000">Last 1000 lines</option>
          <option :value="5000">Last 5000 lines</option>
        </select>
        <button class="btn-outline sm" @click="loadLog()">
          <i class="fa fa-search"></i> Apply
        </button>
        <span class="line-count" v-if="!loading">{{ totalLines }} matching lines</span>
      </div>

      <div class="state-box" v-if="loading">
        <i class="fa fa-spinner fa-spin"></i> Loading…
      </div>
      <div class="state-box" v-else-if="lines.length === 0">
        <i class="fa fa-shield-alt"></i> No audit log entries yet
      </div>
      <pre class="log-content" v-else ref="logContent">{{ lines.join('\n') }}</pre>

    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { FETCH_SYSLOG_CONTENT } from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Syslog", route: "" }
      ],
      loading: false,
      lines: [],
      totalLines: 0,
      search: "",
      linesLimit: 500
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadLog();
  },
  methods: {
    loadLog() {
      this.loading = true;
      this.$store
        .dispatch(FETCH_SYSLOG_CONTENT, { lines: this.linesLimit, search: this.search })
        .then(data => {
          this.lines = (data && data.lines) || [];
          this.totalLines = (data && data.total_lines) || 0;
          this.loading = false;
          this.$nextTick(() => {
            const el = this.$refs.logContent;
            if (el) el.scrollTop = el.scrollHeight;
          });
        })
        .catch(() => {
          this.lines = [];
          this.loading = false;
          this.$toastr.e("Failed to load audit log");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: $blue;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f0f9ff; }
  &.sm { height: 32px; padding: 0 12px; }
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f2f8;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;

  i { color: #a0aab8; font-size: 12px; }

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 12.5px;
    font-family: $font;
    color: $dark;
    background: transparent;
    min-width: 0;

    &::placeholder { color: #b0bac9; }
  }
}

select {
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 26px 0 10px;
  font-size: 12px;
  font-family: $font;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 8px center;
  appearance: none;
  cursor: pointer;

  &:focus { outline: none; border-color: $blue; }
}

.line-count {
  font-size: 11.5px;
  color: #a0aab8;
  margin-left: auto;
}

.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
}

.log-content {
  margin: 0;
  padding: 16px 20px;
  background: #0d1b3e;
  color: #d6e4f0;
  font-size: 11.5px;
  font-family: 'SF Mono', Consolas, monospace;
  line-height: 1.6;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 640px;
}
</style>
